import { AlertTriangle, ArrowDown, ArrowUp, Crosshair, Plus, Trash2 } from "lucide-react";
import { uid } from "@/lib/chat";

const SPEAKERS = { them: "Them", me: "Me", unknown: "Who?" };
const NEXT = { them: "me", me: "them", unknown: "them" };
const STYLE = {
  them: "bg-indigo-500/15 text-indigo-600 dark:text-indigo-300 border-indigo-500/30",
  me: "bg-primary/15 text-primary border-primary/30",
  unknown: "bg-amber-500/15 text-amber-600 dark:text-amber-300 border-amber-500/40",
};

const Row = ({ m, i, total, isLatest, update, move, remove, setLatest }) => (
  <div data-testid={`transcript-message-item-${i}`} className={`group flex gap-2 items-start rounded-2xl p-2 transition-colors ${isLatest ? "bg-accent/10 ring-1 ring-accent/40" : "hover:bg-secondary/40"}`}>
    <button data-testid={`transcript-speaker-toggle-${i}`} onClick={() => update(m.id, { speaker: NEXT[m.speaker] })} title="Switch speaker"
      className={`shrink-0 mt-1 w-14 h-7 rounded-full border text-xs font-semibold transition-colors ${STYLE[m.speaker]}`}>
      {SPEAKERS[m.speaker]}
    </button>
    <textarea translate="no" data-testid={`transcript-message-input-${i}`} value={m.text} rows={1} maxLength={1000}
      onChange={(e) => update(m.id, { text: e.target.value })}
      className="flex-1 min-w-0 resize-none bg-transparent text-sm py-1.5 px-2 rounded-lg border border-transparent focus:border-border focus:bg-card outline-none [field-sizing:content]" />
    <div className="flex shrink-0 items-center gap-0.5 opacity-80 group-hover:opacity-100">
      {m.speaker !== "me" && (
        <button data-testid={`transcript-set-latest-${i}`} onClick={() => setLatest(m.id)} title="Reply to this message"
          className={`h-7 w-7 rounded-lg flex items-center justify-center ${isLatest ? "text-accent" : "text-muted-foreground hover:text-foreground"}`}><Crosshair className="h-3.5 w-3.5" /></button>
      )}
      <button data-testid={`transcript-move-up-${i}`} disabled={i === 0} onClick={() => move(i, -1)} aria-label="Move up" className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-25"><ArrowUp className="h-3.5 w-3.5" /></button>
      <button data-testid={`transcript-move-down-${i}`} disabled={i === total - 1} onClick={() => move(i, 1)} aria-label="Move down" className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-25"><ArrowDown className="h-3.5 w-3.5" /></button>
      <button data-testid={`transcript-delete-message-button-${i}`} onClick={() => remove(m.id)} aria-label="Delete" className="h-7 w-7 rounded-lg flex items-center justify-center text-muted-foreground hover:text-rose-500"><Trash2 className="h-3.5 w-3.5" /></button>
    </div>
  </div>
);

export const TranscriptEditor = ({ messages, setMessages, latestId, setLatestId, ambiguous, notes }) => {
  const update = (id, patch) => setMessages((ms) => ms.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  const move = (i, d) => setMessages((ms) => { const n = [...ms]; [n[i], n[i + d]] = [n[i + d], n[i]]; return n; });
  const remove = (id) => setMessages((ms) => ms.filter((m) => m.id !== id));
  const add = () => setMessages((ms) => [...ms, { id: uid(), speaker: "them", text: "" }]);
  const unknown = messages.filter((m) => m.speaker === "unknown").length;
  const latest = messages.find((m) => m.id === latestId);

  return (
    <div className="panel p-5 sm:p-6" data-testid="transcript-editor">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold">Conversation</h3>
          <p className="text-xs text-muted-foreground">Check speakers and fix any mistakes. Tap the target to choose which message to reply to.</p>
        </div>
        <span className="text-xs text-muted-foreground shrink-0" data-testid="transcript-count">{messages.length} msgs</span>
      </div>

      {(ambiguous || unknown > 0) && messages.length > 0 && (
        <div data-testid="transcript-ambiguity-banner" className="mb-3 flex gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-xs">
          <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          <span>{unknown > 0 ? `${unknown} message(s) need a speaker label. ` : ""}Please confirm who said what and that the highlighted message is the one you're replying to.{notes ? ` Note: ${notes}` : ""}</span>
        </div>
      )}

      {messages.length === 0 ? (
        <div data-testid="transcript-empty" className="rounded-2xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No messages yet. Add a screenshot, paste the chat or type their latest message above.
        </div>
      ) : (
        <div className="space-y-1 max-h-[420px] overflow-y-auto pr-1">
          {messages.map((m, i) => (
            <Row key={m.id} m={m} i={i} total={messages.length} isLatest={m.id === latestId} update={update} move={move} remove={remove} setLatest={setLatestId} />
          ))}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <button data-testid="transcript-add-message-button" onClick={add} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"><Plus className="h-4 w-4" />Add message</button>
        {latest && <div data-testid="transcript-latest-indicator" className="text-xs text-muted-foreground truncate max-w-[70%]">Replying to: <span className="text-foreground">“{latest.text.slice(0, 60)}”</span></div>}
      </div>
    </div>
  );
};
